import { ApprovalStatus, formatCurrency } from "@employee-management/common";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiService } from "@/lib/api-service";
import { parseValidationErrors } from "@/lib/parse-validation-errors.util";
import type { OvertimeEntry } from "@/types/project.types";
import { toast } from "sonner";

const approvalFormSchema = z
  .object({
    approvalStatus: z.nativeEnum(ApprovalStatus),
    rejectionComment: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.approvalStatus === ApprovalStatus.REJECTED) {
      if (!data.rejectionComment || data.rejectionComment.trim().length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rejectionComment"],
          message: "Rejection comment is required",
        });
      }
    }
  });

type ApprovalFormData = z.infer<typeof approvalFormSchema>;

interface OvertimeApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overtimeEntry: OvertimeEntry;
  onComplete: () => void;
  isProjectView?: boolean;
}

export function OvertimeApprovalDialog({
  open,
  onOpenChange,
  overtimeEntry,
  onComplete,
  isProjectView,
}: OvertimeApprovalDialogProps) {
  const [action, setAction] = useState<"approve" | "reject" | null>(null);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    getFieldState,
    setValue,
  } = useForm<ApprovalFormData>({
    resolver: zodResolver(approvalFormSchema),
    defaultValues: {
      approvalStatus: ApprovalStatus.PENDING,
      rejectionComment: "",
    },
  });

  const approvalMutation = useMutation({
    mutationFn: async (data: ApprovalFormData) => {
      const payload = {
        approvalStatus: data.approvalStatus,
        ...(data.approvalStatus === ApprovalStatus.REJECTED && {
          rejectionComment: data.rejectionComment,
        }),
      };

      await apiService.patch(`/overtime/${overtimeEntry.id}/approval`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["overtime", "pending"] });
      queryClient.invalidateQueries({ queryKey: ["overtime", "project"] });
      // Also invalidate the specific project to update its updatedAt timestamp
      if (overtimeEntry.project?.id) {
        queryClient.invalidateQueries({
          queryKey: ["project", overtimeEntry.project.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["project-revision-history", overtimeEntry.project.id],
        });
      }
      reset();
      onComplete();
    },
    onError: (error: unknown) => {
      parseValidationErrors<ApprovalFormData>(error, setError, getFieldState);

      toast.error("Failed to update approval status");
    },
  });

  const onSubmit = (data: ApprovalFormData) => {
    const approvalStatus =
      action === "approve" ? ApprovalStatus.APPROVED : ApprovalStatus.REJECTED;

    approvalMutation.mutate({
      ...data,
      approvalStatus,
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAction(null);
      reset();
    }
    onOpenChange(newOpen);
  };

  const handleActionClick = (newAction: "approve" | "reject") => {
    setAction(newAction);
    setValue(
      "approvalStatus",
      newAction === "approve"
        ? ApprovalStatus.APPROVED
        : ApprovalStatus.REJECTED,
      { shouldValidate: false }
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const calculateTotalHours = (employees: OvertimeEntry["employees"]) => {
    return employees.reduce((total, emp) => total + emp.hours, 0);
  };

  const calculateTotalAmount = (employees: OvertimeEntry["employees"]) => {
    return employees.reduce((total, emp) => total + emp.total, 0);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {action === "approve" && "Approve Overtime Entry"}
            {action === "reject" && "Reject Overtime Entry"}
            {!action && "Review Overtime Entry"}
          </DialogTitle>
          <DialogDescription>
            {action === "approve" &&
              "Are you sure you want to approve this overtime entry?"}
            {action === "reject" &&
              "Please provide a reason for rejecting this overtime entry."}
            {!action && "Review the overtime entry details below."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Overtime Entry Details */}
          <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg">
            <div>
              <Label className="text-sm font-medium">Date</Label>
              <p className="text-sm text-muted-foreground">
                {formatDate(overtimeEntry.date)}
              </p>
            </div>
            {!isProjectView && (
              <>
                <div>
                  <Label className="text-sm font-medium">Project</Label>
                  <p className="text-sm text-muted-foreground">
                    {overtimeEntry.project.name}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Client</Label>
                  <p className="text-sm text-muted-foreground">
                    {overtimeEntry.project.clientName}
                  </p>
                </div>
              </>
            )}
            <div>
              <Label className="text-sm font-medium">Total Hours</Label>
              <p className="text-sm text-muted-foreground">
                {calculateTotalHours(overtimeEntry.employees).toFixed(1)}h
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">Total Amount</Label>
              <p className="text-sm text-muted-foreground">
                ${calculateTotalAmount(overtimeEntry.employees).toFixed(2)}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">Created By</Label>
              <p className="text-sm text-muted-foreground">
                {overtimeEntry.createdByRef?.name || "Unknown"}
              </p>
            </div>
          </div>

          {/* Employees List */}
          <div>
            <Label className="text-sm font-medium">Employees</Label>
            <div className="mt-2 space-y-2">
              {overtimeEntry.employees.map((emp) => (
                <div
                  key={emp.employee.email}
                  // className="flex justify-between items-center p-2 border rounded"
                  className="flex flex-col justify-evenly items-start sm:flex sm:flex-row sm:justify-between sm:items-center p-2 border rounded"
                >
                  <div>
                    <span className="font-medium">{emp.employee.name}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      {emp.employee.email}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span>
                      {emp.hours}h @ {formatCurrency(emp.rate)}/hr
                    </span>
                    <span className="ml-2 font-medium">
                      = {formatCurrency(emp.total)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {overtimeEntry.notes && (
            <div className="overflow-hidden">
              <Label className="text-sm font-medium">Notes</Label>
              <p className="text-sm text-muted-foreground mt-1 break-words">
                {overtimeEntry.notes}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          {!action && (
            <div className="flex space-x-2">
              <Button
                onClick={() => handleActionClick("approve")}
                className="flex-1 bg-green-600 text-white hover:bg-green-700"
              >
                Approve
              </Button>
              <Button
                onClick={() => handleActionClick("reject")}
                variant="destructive"
                className="flex-1"
              >
                Reject
              </Button>
            </div>
          )}

          {/* Rejection Comment Form */}
          {action === "reject" && (
            <div className="space-y-2">
              <Label
                required
                htmlFor="rejectionComment"
                className="text-sm font-medium"
              >
                Rejection Reason
              </Label>
              <Textarea
                id="rejectionComment"
                placeholder="Please provide a reason for rejecting this overtime entry..."
                {...register("rejectionComment")}
                className="min-h-[100px]"
              />
              {errors.rejectionComment && (
                <p className="text-sm text-red-500">
                  {errors.rejectionComment.message}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {action && (
            <>
              <Button
                variant="outline"
                onClick={() => setAction(null)}
                disabled={approvalMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={approvalMutation.isPending}
                className={
                  action === "approve"
                    ? "bg-green-600  hover:bg-green-700"
                    : "bg-red-600 text-white hover:bg-red-700"
                }
              >
                {approvalMutation.isPending
                  ? "Processing..."
                  : action === "approve"
                    ? "Approve"
                    : "Reject"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
